import {
  type DependencyList,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

const NEAR_BOTTOM_PX = 80

function isNearBottom(element: HTMLElement, threshold: number): boolean {
  const distanceFromBottom =
    element.scrollHeight - element.scrollTop - element.clientHeight
  return distanceFromBottom <= threshold
}

function isLayoutDrivenScroll({
  previousClientHeight,
  nextClientHeight,
  previousScrollHeight,
  nextScrollHeight,
}: {
  previousClientHeight: number | null
  nextClientHeight: number
  previousScrollHeight: number | null
  nextScrollHeight: number
}): boolean {
  const hadViewport = previousClientHeight !== null && previousClientHeight > 0
  const hadContent = previousScrollHeight !== null && previousScrollHeight > 0
  const viewportResized =
    hadViewport && previousClientHeight !== nextClientHeight
  const contentResized = hadContent && previousScrollHeight !== nextScrollHeight
  return viewportResized || contentResized
}

function writeScrollTop({
  container,
  isRestoringRef,
  lastClientHeightRef,
  lastScrollHeightRef,
  top,
}: {
  container: HTMLDivElement | null
  isRestoringRef: { current: boolean }
  lastClientHeightRef: { current: number | null }
  lastScrollHeightRef: { current: number | null }
  top: number
}): void {
  if (!container) return
  isRestoringRef.current = true
  container.scrollTop = top
  lastClientHeightRef.current = container.clientHeight
  lastScrollHeightRef.current = container.scrollHeight
  isRestoringRef.current = false
}

export interface UseStickToBottomParams {
  contentRef: RefObject<HTMLElement | null>
  scrollDeps: DependencyList
  resetWhenEmpty: boolean
  nearBottomPx?: number
  alwaysStick?: boolean
}

export interface UseStickToBottomResult {
  scrollRef: (node: HTMLDivElement | null) => void
  onScroll: () => void
  isPinned: boolean
  hasNewContent: boolean
  scrollToLatest: () => void
}

export function useStickToBottom({
  contentRef,
  scrollDeps,
  resetWhenEmpty,
  nearBottomPx = NEAR_BOTTOM_PX,
  alwaysStick = false,
}: UseStickToBottomParams): UseStickToBottomResult {
  const scrollElementRef = useRef<HTMLDivElement | null>(null)
  const [isPinned, setIsPinned] = useState(true)
  const [hasNewContent, setHasNewContent] = useState(false)
  const isPinnedRef = useRef(true)
  const isRestoringRef = useRef(false)
  const isLayoutAdjustingRef = useRef(false)
  const lastClientHeightRef = useRef<number | null>(null)
  const lastScrollHeightRef = useRef<number | null>(null)

  const scrollRef = useCallback((node: HTMLDivElement | null) => {
    scrollElementRef.current = node
  }, [])

  const restoreScrollTop = useCallback((top: number) => {
    writeScrollTop({
      container: scrollElementRef.current,
      isRestoringRef,
      lastClientHeightRef,
      lastScrollHeightRef,
      top,
    })
  }, [])

  const finishLayoutAdjust = useCallback(() => {
    isLayoutAdjustingRef.current = false
  }, [])

  const scrollToBottom = useCallback(() => {
    if (!isPinnedRef.current) {
      finishLayoutAdjust()
      return
    }
    window.requestAnimationFrame(() => {
      if (!isPinnedRef.current) {
        finishLayoutAdjust()
        return
      }
      const container = scrollElementRef.current
      if (!container) {
        finishLayoutAdjust()
        return
      }
      restoreScrollTop(container.scrollHeight)
      finishLayoutAdjust()
    })
  }, [finishLayoutAdjust, restoreScrollTop])

  const scrollToLatest = useCallback(() => {
    isPinnedRef.current = true
    setIsPinned(true)
    setHasNewContent(false)
    const container = scrollElementRef.current
    if (!container) return
    window.requestAnimationFrame(() => {
      restoreScrollTop(container.scrollHeight)
    })
  }, [restoreScrollTop])

  const onScroll = useCallback(() => {
    if (alwaysStick) return
    const container = scrollElementRef.current
    if (!container || isRestoringRef.current) return
    // Inset-preview shrink must not unpin; browser scroll can land before rAF restore.
    if (isLayoutAdjustingRef.current && isPinnedRef.current) return

    const nextClientHeight = container.clientHeight
    const nextScrollHeight = container.scrollHeight
    const keepPin =
      isPinnedRef.current &&
      isLayoutDrivenScroll({
        previousClientHeight: lastClientHeightRef.current,
        nextClientHeight,
        previousScrollHeight: lastScrollHeightRef.current,
        nextScrollHeight,
      })
    lastClientHeightRef.current = nextClientHeight
    lastScrollHeightRef.current = nextScrollHeight
    if (keepPin) return

    const pinned = isNearBottom(container, nearBottomPx)
    isPinnedRef.current = pinned
    setIsPinned(pinned)
    if (pinned) setHasNewContent(false)
  }, [alwaysStick, nearBottomPx])

  useLayoutEffect(() => {
    if (isPinnedRef.current) {
      scrollToBottom()
    } else {
      setHasNewContent(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scrollDeps is the caller-owned content key
  }, [scrollToBottom, ...scrollDeps])

  useEffect(() => {
    const container = scrollElementRef.current
    if (!container || !resetWhenEmpty) return
    isPinnedRef.current = true
    setIsPinned(true)
    setHasNewContent(false)
    restoreScrollTop(0)
  }, [resetWhenEmpty, restoreScrollTop])

  useLayoutEffect(() => {
    const content = contentRef.current
    if (!content) return
    const observer = new ResizeObserver(() => {
      if (isPinnedRef.current) {
        scrollToBottom()
      } else {
        setHasNewContent(true)
      }
    })
    observer.observe(content)
    return () => observer.disconnect()
  }, [contentRef, scrollToBottom])

  useLayoutEffect(() => {
    const container = scrollElementRef.current
    if (!container) return
    const observer = new ResizeObserver(() => {
      if (!isPinnedRef.current) return
      isLayoutAdjustingRef.current = true
      scrollToBottom()
    })
    observer.observe(container)
    return () => observer.disconnect()
  }, [scrollToBottom])

  return { scrollRef, onScroll, isPinned, hasNewContent, scrollToLatest }
}
